import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Radio } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePW9319Query } from './hooks/usePW9319Query';
import type { CardCustodyRow } from '../../services/pw9319Api';
import './PW9319Form.css';

const columns: ColumnsType<CardCustodyRow> = [
  { title: '卡號', dataIndex: 'F919_01', key: 'F919_01', width: 110 },
  { title: '員工代號', dataIndex: 'F919_11', key: 'F919_11', width: 90 },
  { title: '員工姓名', dataIndex: 'F901_12', key: 'F901_12', width: 100, ellipsis: true },
  { title: '生效日期', dataIndex: 'F919_12', key: 'F919_12', width: 105 },
  { title: '失效日期', dataIndex: 'F919_13', key: 'F919_13', width: 105 },
  { title: '建立者', dataIndex: 'F919_91', key: 'F919_91', width: 90 },
  { title: '建立日期', dataIndex: 'F919_92', key: 'F919_92', width: 105 },
];

const PW9319Form: React.FC = () => {
  const { loading, data, queried, noData, runQuery, reset } = usePW9319Query();

  const [empFrom, setEmpFrom] = useState('');
  const [empTo, setEmpTo] = useState('');
  const [sortOrder, setSortOrder] = useState<'card' | 'emp'>('card');

  const handleQuery = () => {
    runQuery({
      empFrom: empFrom.trim() || undefined,
      empTo: empTo.trim() || undefined,
      sortOrder,
    });
  };

  const handleReset = () => {
    setEmpFrom('');
    setEmpTo('');
    setSortOrder('card');
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    const sortLabel = sortOrder === 'card' ? '卡號別' : '員工代號別';
    const rows = data
      .map(
        r => `<tr>
        <td>${r.F919_01}</td>
        <td>${r.F919_11}</td>
        <td>${r.F901_12}</td>
        <td>${r.F919_12}</td>
        <td>${r.F919_13}</td>
        <td>${r.F919_91}</td>
        <td>${r.F919_92}</td>
      </tr>`
      )
      .join('');
    win.document.write(`<!DOCTYPE html><html><head>
<title>感應卡-保管清冊（${sortLabel}）</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}
h2{text-align:center}table{width:100%;border-collapse:collapse}
th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}
@media print{body{margin:8mm}}</style></head><body>
<h2>感應卡-保管清冊（${sortLabel}）</h2>
<table><thead><tr>
<th>卡號</th><th>員工代號</th><th>員工姓名</th>
<th>生效日期</th><th>失效日期</th><th>建立者</th><th>建立日期</th>
</tr></thead><tbody>${rows}</tbody></table>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const rowKey = (r: CardCustodyRow) => `${r.F919_01}_${r.F919_11}`;

  return (
    <PermissionGuard module={Modules.PW9319} action="query">
      <div className="pw9319-container">
        <div className="pw9319-header">
          <h2>感應卡-保管清冊</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>
              列印
            </Button>
          )}
        </div>

        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div className="pw9319-filter-item">
              <label className="pw9319-label">人員代號（從）</label>
              <Input
                value={empFrom}
                onChange={e => setEmpFrom(e.target.value)}
                placeholder="起始人員"
                style={{ width: 130 }}
                allowClear
              />
            </div>
            <div className="pw9319-filter-item">
              <label className="pw9319-label">人員代號（至）</label>
              <Input
                value={empTo}
                onChange={e => setEmpTo(e.target.value)}
                placeholder="結束人員"
                style={{ width: 130 }}
                allowClear
              />
            </div>
            <div className="pw9319-filter-item">
              <label className="pw9319-label">排序方式</label>
              <Radio.Group
                value={sortOrder}
                onChange={e => setSortOrder(e.target.value as 'card' | 'emp')}
              >
                <Radio value="card">卡號別</Radio>
                <Radio value="emp">員工代號別</Radio>
              </Radio.Group>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleQuery}
                  loading={loading}
                >
                  查詢
                </Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>
                  清除
                </Button>
              </Space>
            </div>
          </Space>
        </Card>

        {queried && noData && (
          <Empty description="查無此資料" style={{ marginTop: 40 }} />
        )}
        {queried && !noData && (
          <Table<CardCustodyRow>
            columns={columns}
            dataSource={data}
            rowKey={rowKey}
            loading={loading}
            size="small"
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showTotal: t => `共 ${t} 筆`,
            }}
            scroll={{ x: 720 }}
          />
        )}
      </div>
    </PermissionGuard>
  );
};

export default PW9319Form;
